import { error } from 'cc';
import Model from '../../../../../common/scripts/Toolkit/Helper/Model';
import TableModel, { PlayType } from './TableModel';
import Delay from '../../../../../common/scripts/Toolkit/Utils/Delay';
import StreamingError from '../../../../../streamingPlayerAdapter/StreamingError';
import DataType from '../Config/DataStructure';
import IHallService from '../Service/IHallService';
import TableGame from '../../../System/TableGame';
import { PathName } from '../Config/PathConfig';
import TableErrorName from '../Config/TableErrorName';
import CommonGamePreviewData from '../DataObject/PreviewData/CommonGamePreviewData';
import SinglePlayGamePreviewData from '../DataObject/PreviewData/SinglePlayGamePreviewData';
import MultiplePlayGamePreviewData from '../DataObject/PreviewData/MultiplePlayGamePreviewData';
import RoundPlayGamePreviewData from '../DataObject/PreviewData/RoundPlayGamePreviewData';
import ITableService from '../Service/ITableService';
import { OverviewDataType, StatsDataType } from '../Config/GamePreviewDataType';

enum EventType {
    ENTER_TABLE = 'ENTER_TABLE',
    EXIT_TABLE = 'EXIT_TABLE',
    OVERVIEW_DATA_RECEIVED = 'OVERVIEW_DATA_RECEIVED',
    STATS_DATA_RECEIVED = 'STATS_DATA_RECEIVED',
    TABLE_LIST_RECEIVED = 'TABLE_LIST_RECEIVED',
}

export enum PlayTypeState {
    OPENED,
    CLOSED,
    COMING_SOON,
}

export type EnterTableOption = {
    tableID: number;
    gameID: string;
    isTrial: boolean;
};

const exitTableErrorList: TableErrorName[] = [TableErrorName.DEVICE_UNAVAILABLE, TableErrorName.DEVICE_RUN_FAILED, TableErrorName.DEVICE_NO_RESULT];

export type GamePreviewData = CommonGamePreviewData | SinglePlayGamePreviewData | MultiplePlayGamePreviewData | RoundPlayGamePreviewData;

export default abstract class HallModel extends Model {
    public static readonly EVENT_TYPE = EventType;

    protected _hallMap: DataType.Hall.OverviewResponseData = null;
    protected _gameDataMap: { [gameKey: string]: GamePreviewData } = {};
    protected _gameAssets: { [gameKey: string]: DataType.Device.AssetInfoData } = {};
    protected _tableModelMap: { [tableID: number]: TableModel } = {};
    protected _currentHallID = -1;
    protected _currentGameID = '';
    protected _currentPlayType: DataType.Enums.PlayType = DataType.Enums.PlayType.Single;
    protected _currentTableID = -1;
    protected _lastTableID = -1;
    protected _nextTableID = -1;
    protected _duringExitTable = false;
    protected _exitManually = false;
    protected _denom: number = 0;
    protected _exitTableError: TableErrorName | StreamingError = null;
    protected _machineInfoMap: { [machineID: string]: DataType.Device.InfoResponseData } = {};
    protected _machineInfoPromiseMap: { [machineID: string]: Promise<DataType.Device.InfoResponseData> } = {};
    protected _bannerConfigs: DataType.Hall.BannerData[] = [];

    constructor(
        protected readonly _activatePlayTypes: { [key in PlayType]: boolean },
        protected readonly _hallService: IHallService & ITableService,
    ) {
        super();
    }

    public get denom() {
        return this._denom ?? 10000;
    }

    public get bannerConfigs(): DataType.Hall.BannerData[] {
        return this._bannerConfigs;
    }

    public get hallList() {
        return Object.values(this._hallMap);
    }

    public get gameList() {
        return Object.values(this._gameDataMap);
    }

    public get tableModelList() {
        return Object.values(this._tableModelMap);
    }

    public get currentHallID() {
        return this._currentHallID;
    }

    public get currentGameID() {
        return this._currentGameID;
    }

    public get currentPlayType() {
        return this._currentPlayType;
    }

    public get currentGameKey() {
        return CommonGamePreviewData.createKey(this._currentHallID, this._currentGameID, this._currentPlayType);
    }

    public get currentGameData(): GamePreviewData {
        return this._gameDataMap[this.currentGameKey];
    }

    public get isInsideTable() {
        return this._currentTableID !== -1;
    }

    public get currentTable() {
        return this._tableModelMap[this._currentTableID];
    }

    /** table after table changed */
    public get nextTable() {
        return this._tableModelMap[this._nextTableID];
    }

    /** table before table changed */
    public get lastTable() {
        return this._tableModelMap[this._lastTableID];
    }

    public get exitTableError() {
        return this._exitTableError;
    }

    public get gameAssets() {
        return this._gameAssets;
    }

    public get duringExitTable() {
        return this._duringExitTable;
    }

    public init(): void {
        TableGame.connection.on(PathName.Login, ({ Denom, AccountCurrency }) => {
            this._denom = Denom ?? 10000;
            TableGame.account.currency = AccountCurrency;
        });
        TableGame.connection.on(`${PathName.Error}_error`, (err: string) => {
            if (exitTableErrorList.includes(err as TableErrorName)) {
                this._exitTableError = err as TableErrorName;
            } else {
                TableGame.error.handle(err);
            }
        });
        TableGame.connection.on(PathName.HallOverview, this.onOverviewDataReceived, this);
        TableGame.connection.on(PathName.HallStats, this.onStatsDataReceived, this);
        TableGame.connection.on(PathName.HallTableList, this.onTableListDataRecevied, this);
        TableGame.connection.on(PathName.EnterTable, this.onEnterTable, this);
        TableGame.connection.on(PathName.ExitTable, this.onExitTable, this);
    }

    public isPlayTypeActivated(playType: PlayType) {
        return this._activatePlayTypes[playType];
    }

    public getHallByID(hallID: number) {
        return this._hallMap[hallID];
    }

    public getGameDataByID(hallID: number, gameID: string, playType: DataType.Enums.PlayType = this._currentPlayType): CommonGamePreviewData {
        const gameKey = CommonGamePreviewData.createKey(hallID, gameID, playType);
        return this._gameDataMap[gameKey];
    }

    public getTableByID(tableID: number): TableModel {
        return this._tableModelMap[tableID];
    }

    public getCurrentTableList(): TableModel[] {
        return this.getTableListOfGame(this._currentHallID, this._currentGameID, this._currentPlayType);
    }

    public getTableListOfGame(hallID: number, gameID: string, playType: DataType.Enums.PlayType): TableModel[] {
        return Object.values(this._tableModelMap).filter((table) => table.hallID === hallID && table.gameID === gameID && table.type === playType);
    }

    public waitForHallOverviewData() {
        if (this._hallMap) {
            return Promise.resolve();
        }
        return Delay.event(HallModel.EVENT_TYPE.OVERVIEW_DATA_RECEIVED, this);
    }

    public async requestTableListOfGame(hallID?: number, gameID?: string, playType?: DataType.Enums.PlayType) {
        if (this._currentHallID === undefined && hallID === undefined) {
            throw new Error('Current hall ID is not set and no hall ID provided');
        }
        if (this._currentGameID === undefined && gameID === undefined) {
            throw new Error('Current game ID is not set and no game ID provided');
        }
        if (this._currentPlayType === undefined && playType === undefined) {
            throw new Error('Current play type is not set and no play type provided');
        }
        this._currentHallID = hallID ?? this._currentHallID;
        this._currentGameID = gameID ?? this._currentGameID;
        this._currentPlayType = playType ?? this._currentPlayType;
        const data = await TableGame.connection.send(PathName.HallTableList, { HallID: this._currentHallID, GameID: this._currentGameID });
        this.onTableListDataRecevied(data);
    }

    public async getMachineInfo(machineID: number) {
        let info = this._machineInfoMap[machineID];
        if (!info) {
            if (this._machineInfoPromiseMap[machineID]) {
                return this._machineInfoPromiseMap[machineID];
            }
            const promise = TableGame.connection.send(PathName.DeviceInfo, { DeviceID: machineID });
            this._machineInfoPromiseMap[machineID] = promise;
            info = await promise;
            this._machineInfoMap[machineID] = info;
        }
        return info;
    }

    public async loadMachineInfoList() {
        const infoList = await TableGame.connection.send(PathName.DeviceList);
        infoList.Data.forEach((info) => {
            this._machineInfoMap[info.ID] = info;
        });
    }

    public async enterTable(enterTableOptions: Partial<EnterTableOption>): Promise<[boolean, string]> {
        const { tableID = -1, isTrial = false } = enterTableOptions;
        if (tableID) {
            if (!this._tableModelMap[tableID]) {
                return [false, 'Table is not existed'];
            }
            if (this._currentTableID === tableID) {
                return [true, null];
            }
        }
        try {
            const postData: DataType.Table.EnterRequestData = {
                TableID: tableID,
                Trial: isTrial,
            };
            const data = await TableGame.connection.send(PathName.EnterTable, postData, {
                expectedErrors: [
                    TableErrorName.TABLE_IS_FULL,
                    TableErrorName.TABLE_IS_CLOSED,
                    TableErrorName.NO_TABLE_FOUND,
                    TableErrorName.TABLE_IS_PREPARING,
                ],
            });
            await Delay.event(TableModel.EVENT_TYPE.ENTER_TABLE, this.currentTable);
            this._currentHallID = data.HallID;
            this._currentGameID = data.GameID;
            this.emit(HallModel.EVENT_TYPE.ENTER_TABLE, data.TableID);
            return [true, null];
        } catch (err) {
            if (err instanceof Error) {
                error(err);
                return [false, err.message];
            }
            if (err === TableErrorName.TABLE_IS_FULL) {
                return [false, `${TableErrorName.TABLE_IS_FULL}`];
            }
            return [false, err];
        }
    }

    public async exitTable(manually: boolean, exitTableError?: TableErrorName | StreamingError) {
        if (!this.isInsideTable) {
            return;
        }
        if (this._duringExitTable) {
            return;
        }
        this._duringExitTable = true;
        this._exitManually = manually;
        this._exitTableError = exitTableError;
        const exitTableID = this._currentTableID;
        await TableGame.connection.send(PathName.ExitTable);
        this.emit(HallModel.EVENT_TYPE.EXIT_TABLE, exitTableID);
        await TableGame.connection.send(PathName.HallTableList, { HallID: this._currentHallID, GameID: this._currentGameID });
    }

    protected onTableListDataRecevied(data: DataType.Hall.TableListResponseData) {
        const gameKey = CommonGamePreviewData.createKey(data.ID, data.GameID, this._currentPlayType);
        const gameData = this._gameDataMap[gameKey];
        if (!gameData) {
            error(`Game data not found for game key: ${gameKey}`);
            return;
        }
        // gameData.updateWithListData(data);
        // update table model list
        const newTableIDList: number[] = [];
        data.TableSummaries.forEach((tableData) => {
            if (!this._tableModelMap[tableData.TableID]) {
                const model = this._hallService.getTableModel(tableData);
                this._tableModelMap[tableData.TableID] = model;
                newTableIDList.push(tableData.TableID);
            }
            this._tableModelMap[tableData.TableID].updateWithSummaryData(tableData);
        });
        this.emit(HallModel.EVENT_TYPE.TABLE_LIST_RECEIVED, newTableIDList, gameKey);
    }

    protected onEnterTable(data: DataType.Table.EnterResponseData) {
        if (this._currentTableID !== -1) {
            this._nextTableID = data.TableID;
            this.currentTable.exit(true);
            this._nextTableID = -1;
        }
        this._currentTableID = data.TableID;
        // create table model if not existed
        if (!this._tableModelMap[data.TableID]) {
            this._tableModelMap[data.TableID] = this._hallService.getTableModel(data);
            this.emit(HallModel.EVENT_TYPE.TABLE_LIST_RECEIVED, [data.TableID], data.GameID);
        }
        this.currentTable.enter(data);
    }

    protected async onExitTable(data: DataType.Table.ExitResponseData) {
        if (this._currentTableID !== data.TableID) {
            return;
        }
        // server 直接傳 exitTable (因 table close)
        if (!this.duringExitTable && this.currentTable.idleTimeout > 0) {
            this._exitManually = false;
            this._exitTableError = TableErrorName.TABLE_IS_CLOSED;
            TableGame.connection.send(PathName.HallTableList, { HallID: this._currentHallID, GameID: this._currentGameID });
        }
        // wait for table list update before exiting the game
        await Delay.event(HallModel.EVENT_TYPE.TABLE_LIST_RECEIVED, TableGame.hall);
        this.currentTable.exit(this._exitManually, this._exitTableError as string);
        this._lastTableID = this._currentTableID;
        this._currentTableID = -1;
        this._duringExitTable = false;
        this._exitManually = false;
        this._exitTableError = undefined;
    }

    protected abstract onOverviewDataReceived(data: DataType.Hall.OverviewResponseData): void;
    protected abstract onStatsDataReceived(data: DataType.Hall.StatsResponseData): void;
    protected abstract createPreviewDataObjectByOverview(originRawData: DataType.Hall.OverviewResponseData): OverviewDataType[];
    protected abstract createPreviewDataByStats(originRawData: DataType.Hall.StatsResponseData): StatsDataType[];
}
